import { Injectable } from '@angular/core';

// Dates are formatted with the venue's own Culture setting ("en-GB", "fr-FR"),
// not just the language, so a French venue gets French month names *and* French
// date ordering. This is the same value the booking wizard already passes to
// toLocaleDateString.
export const DEFAULT_DATE_LOCALE = 'en-GB';

/**
 * Holds the venue's locale for the life of the page.
 *
 * The venue's settings are fetched once, before the app renders (see
 * venue-language.ts), and the two things worth keeping from that answer live
 * here: the language ngx-translate is using, and the full Culture string used
 * for date formatting.
 *
 * This exists because the Culture setting is otherwise read per component, from
 * a venuesettings call each one makes for itself - so anything rendered outside
 * those components, the calendar's month heading included, had nothing to format
 * against and silently fell back to English.
 */
@Injectable({ providedIn: 'root' })
export class VenueLocale {
  /** Full culture string, e.g. "fr-FR". Falls back to en-GB. */
  dateLocale: string = DEFAULT_DATE_LOCALE;

  /** Language code the translations are loaded for, e.g. "fr". */
  language: string = 'en';

  set(culture: string | null | undefined, language: string): void {
    const raw = (culture ?? '').trim();
    const usable = raw !== '' && raw !== 'null' && raw !== 'undefined';

    // A venue set to a culture we have no translations for still gets its own
    // date formatting - only the wording falls back to English.
    this.dateLocale = usable ? raw : DEFAULT_DATE_LOCALE;
    this.language = language;
  }
}
